import { XMarkIcon } from '@heroicons/react/24/outline';
import { FormEvent, ReactNode, useEffect, useId, useRef } from 'react';
import { Modal } from 'react-daisyui';
import './FormDialog.styles.scss';

/** What asked the dialog to close: its X, Escape, or a click on the backdrop. */
export type tFormDialogCloseVia = 'close_button' | 'escape' | 'backdrop';

export type tFormDialogProps = {
  /** Unique among open dialogs: the title and the description take their ids from it. */
  id: string;
  title: string;
  /** Shown before the title, out of the dialog's accessible name. */
  icon?: ReactNode;
  description?: string;
  /** A request is in flight: the dialog cannot be closed or sent again. */
  busy?: boolean;
  onClose: (via: tFormDialogCloseVia) => void;
  /** Runs on Enter in a field and on the submit button. */
  onSubmit: () => void;
  /** The buttons, the submit one with `type="submit"`. */
  actions: ReactNode;
  /**
   * Where the focus goes on close when what opened the dialog is gone (the
   * dialog removed it). Without it the focus is left to the browser.
   */
  fallbackFocus?: () => HTMLElement | null;
  children?: ReactNode;
  /** The accessible name of the close button, in the caller's namespace. */
  closeLabel?: string;
  /** The BEM block of the dialog's parts. @default 'FormDialog' */
  block?: string;
};

/**
 * The dialogs open now, oldest first. Escape and Tab are read on the document,
 * so with one dialog over another (the options editor over the result form)
 * only the top one may answer them.
 */
const openDialogs: string[] = [];

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * The form dialog of the app: one shell for the dialogs that ask for
 * something (a lead, a view's name, a result of LEA-50). Mount it while it is
 * open. It started as the Directory v2 dialog and moved here for the chat.
 *
 * The DS has no React modal yet, so the keyboard contract of its
 * accessibility chapter is kept here: a dialog labelled by its title, Escape
 * closes, Tab stays inside, and focus returns to what opened it. It opens on
 * its first field, or on the control marked `data-autofocus` (the safe answer
 * of a confirmation).
 */
export default function FormDialog(props: tFormDialogProps): JSX.Element {
  const {
    id,
    title,
    icon,
    description,
    busy = false,
    onClose,
    onSubmit,
    actions,
    fallbackFocus,
    children,
    closeLabel,
    block = 'FormDialog',
  } = props;
  const instance = useId();
  const formRef = useRef<HTMLFormElement>(null);

  const fallbackFocusRef = useRef(fallbackFocus);
  fallbackFocusRef.current = fallbackFocus;

  useEffect(() => {
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const form = formRef.current;
    const first =
      form?.querySelector<HTMLElement>('[data-autofocus]') ??
      form?.querySelector<HTMLElement>('input, select, textarea') ??
      form?.querySelector<HTMLElement>(FOCUSABLE);

    first?.focus();

    return () => {
      const target =
        opener && document.contains(opener)
          ? opener
          : (fallbackFocusRef.current?.() ?? null);

      target?.focus();
    };
  }, []);

  // The keyboard is read on the document, not on the form: a control that
  // closes its own list (the phone's country) can leave the focus on the page,
  // and Escape and Tab must still belong to the dialog. A key that a control
  // inside handled for itself and stopped never reaches the document.
  const latest = useRef({ busy, onClose });
  latest.current = { busy, onClose };

  useEffect(() => {
    openDialogs.push(instance);

    function handleKeyDown(event: KeyboardEvent) {
      const form = formRef.current;
      if (!form || openDialogs[openDialogs.length - 1] !== instance) return;

      if (event.key === 'Escape') {
        if (!latest.current.busy) latest.current.onClose('escape');
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(
        form.querySelectorAll<HTMLElement>(FOCUSABLE),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !active || !form.contains(active);

      if (event.shiftKey && (outside || active === first)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && (outside || active === last)) {
        event.preventDefault();
        first?.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      openDialogs.splice(openDialogs.indexOf(instance), 1);
    };
  }, [instance]);

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!busy) onSubmit();
  }

  return (
    <Modal.Legacy
      open
      onClickBackdrop={busy ? undefined : () => onClose('backdrop')}
      className={block}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      aria-describedby={description ? `${id}-description` : undefined}
    >
      <form
        ref={formRef}
        className={`${block}__Form`}
        noValidate
        onSubmit={handleSubmit}
      >
        <div className={`${block}__Header`}>
          <div className={`${block}__Heading`}>
            <h2 id={`${id}-title`} className={`${block}__Title`}>
              {icon ? (
                <span className={`${block}__Icon`} aria-hidden="true">
                  {icon}
                </span>
              ) : null}
              {title}
            </h2>
            {description ? (
              <p id={`${id}-description`} className={`${block}__Description`}>
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className={`${block}__Close`}
            aria-label={closeLabel}
            disabled={busy}
            onClick={() => onClose('close_button')}
          >
            <XMarkIcon aria-hidden />
          </button>
        </div>
        <div className={`${block}__Body`}>{children}</div>
        <div className={`${block}__Actions`}>{actions}</div>
      </form>
    </Modal.Legacy>
  );
}

export type tFormFieldProps = {
  /** The control's id: the label points at it, and its error is `<id>-error`. */
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: ReactNode;
  /** The BEM block of the dialog it sits in. @default 'FormDialog' */
  block?: string;
};

/**
 * A labelled control of a form dialog. The control itself says it is
 * required (`aria-required`) and points at its error (`aria-describedby`).
 */
export function FormField(props: tFormFieldProps): JSX.Element {
  const {
    id,
    label,
    required = false,
    error,
    children,
    block = 'FormDialog',
  } = props;

  return (
    <div className={`${block}__Field`}>
      <label htmlFor={id} className={`${block}__Label`}>
        {label}
        {required ? (
          <span className={`${block}__Required`} aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} className={`${block}__FieldError`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
